import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../hooks/useTelegram';
import { useNav } from '../../store/nav';
import { useToast } from '../../store/toast';
import { useProfile } from '../../store/profile';
import { useMe } from '../../hooks/useMe';
import { patchBiometric } from '../../lib/me';
import { biometricAvailable, biometricSupported, runBiometric } from '../../lib/biometric';
import { FigIcon } from '../FigIcon';

const GREEN = 'rgb(var(--positive))';
const BLUE = 'rgb(var(--accent))';

/** Face ID / biometric enablement screen (Profile → Biometrics). Uses Telegram's
 *  BiometricManager on device; in the browser preview it runs a demo path. */
export function BiometricScreen() {
  const { t } = useTranslation();

  // Use the REAL Telegram BiometricManager whenever it exists — including a dev
  // build opened inside real Telegram (via the tunnel). Only a plain browser
  // (no BiometricManager) falls back to the demo path so the flow stays viewable.
  // (Previously this also keyed on import.meta.env.DEV, which wrongly forced the
  // demo path in real Telegram during dev — so Face ID never actually prompted.)
  const demo = !biometricAvailable();

  const push = useNav((s) => s.push);
  const pop = useNav((s) => s.pop);
  const showToast = useToast((s) => s.show);
  const { me, token, refresh } = useMe();
  const localEnabled = useProfile((s) => s.biometricEnabled);
  const localPin = useProfile((s) => s.pinSet);
  const setBiometric = useProfile((s) => s.setBiometric);
  const enabled = me?.biometricEnabled ?? localEnabled;
  // A PIN is mandatory before biometric (server enforces it too, 422 pin_required).
  const pinSet = me?.pinSet ?? localPin;
  const [state, setState] = useState<'checking' | 'ready' | 'unsupported'>('checking');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (demo) {
      setState('ready');
      return;
    }
    biometricSupported().then((ok) => alive && setState(ok ? 'ready' : 'unsupported'));
    return () => {
      alive = false;
    };
  }, [demo]);

  const enable = async () => {
    if (busy) return;
    // PIN required first — send the user to set one, then come back to enable.
    if (!pinSet) {
      haptic();
      push({ name: 'pin' });
      return;
    }
    haptic();
    setBusy(true);
    const ok = demo ? true : await runBiometric(t('biometricFlow.reason'));
    if (!ok) {
      setBusy(false);
      return;
    }
    // Only reflect "enabled" once the server accepts it (rejects without a PIN).
    try {
      if (token) await patchBiometric(token, true);
      setBiometric(true);
      refresh();
      setBusy(false);
      showToast(t('biometricFlow.enabledToast'));
      pop();
      return;
    } catch {
      /* server rejected (e.g. pin_required) — leave it off */
    }
    setBusy(false);
  };

  const disable = async () => {
    if (busy) return;
    haptic();
    setBusy(true);
    // Turning OFF a security feature must be authenticated too — prompt Face ID
    // first; only disable if it succeeds.
    const ok = demo ? true : await runBiometric(t('biometricFlow.reason'));
    if (!ok) {
      setBusy(false);
      return;
    }
    try {
      if (token) await patchBiometric(token, false);
      setBiometric(false);
      refresh();
      setBusy(false);
      showToast(t('biometricFlow.disabledToast'));
      pop();
      return;
    } catch {
      /* leave it on if the server rejects */
    }
    setBusy(false);
  };

  const unsupported = state === 'unsupported';
  const accent = enabled ? GREEN : BLUE;
  const title = enabled
    ? t('biometricFlow.enabledTitle')
    : unsupported
      ? t('biometricFlow.unavailableTitle')
      : t('biometricFlow.enableTitle');
  const subtitle = enabled
    ? t('biometricFlow.enabledSubtitle')
    : unsupported
      ? t('biometricFlow.unavailableSubtitle')
      : t('biometricFlow.subtitle');

  return (
    <div className="push-enter relative flex min-h-screen flex-col bg-surface">
      <div className="flex flex-1 flex-col items-center justify-center px-4">
        <div className="flex flex-col items-center gap-4">
          <span
            className="grid size-20 place-items-center rounded-full bg-card"
            style={{ color: unsupported ? 'rgb(var(--muted))' : accent }}
          >
            <FigIcon name="pfFaceId" size={40} />
          </span>
          <h1 className="text-[24px] font-medium text-ink">{title}</h1>
          <p className="w-[277px] text-center text-[15px] leading-[1.44] text-muted">{subtitle}</p>
        </div>

        {state !== 'checking' && !unsupported && (
          <div className="mt-10 flex w-full flex-col items-center gap-4">
            {enabled ? (
              <button
                onClick={disable}
                disabled={busy}
                className="flex items-center gap-2 text-[15px] font-medium text-negative active:opacity-70 disabled:opacity-60"
              >
                {busy && (
                  <span className="size-4 animate-spin rounded-full border-2 border-negative/30 border-t-negative" />
                )}
                {t('biometricFlow.disable')}
              </button>
            ) : !pinSet ? (
              <>
                <button
                  onClick={() => {
                    haptic();
                    push({ name: 'pin' });
                  }}
                  className="flex h-14 w-full items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:opacity-90"
                >
                  {t('biometricFlow.setPinFirst')}
                </button>
                <p className="w-[277px] text-center text-[13px] leading-[1.44] text-muted">
                  {t('biometricFlow.pinRequiredHint')}
                </p>
              </>
            ) : (
              <button
                onClick={enable}
                disabled={busy}
                className={`flex h-14 w-full items-center justify-center gap-2 rounded-full border border-card bg-accent text-[15px] font-bold text-white ${
                  busy ? 'opacity-60' : 'active:opacity-90'
                }`}
              >
                {busy && (
                  <span className="size-4 animate-spin rounded-full border-2 border-card/40 border-t-white" />
                )}
                {t('biometricFlow.enable')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
